import { createContext, memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import Pusher, { Channel } from 'pusher-js';

import { getPusherSocketOptions } from '@common/pusherSocketOptions';
import { GlobalConstants } from '@constants/global';

interface PusherContextValue {
    pusher: Pusher | null;
    subscribeToChannel: (channelName: string) => Channel | null;
    unsubscribeFromChannel: (channelName: string) => void;
}

const PusherContext = createContext<PusherContextValue>({
    pusher: null,
    subscribeToChannel: () => null,
    unsubscribeFromChannel: () => null,
});

interface PusherProviderProps {
    children: React.ReactNode;
    userId: number;
    token: string;
    apiUrl?: string;
}

export const PusherProvider: React.FC<PusherProviderProps> = memo(
    ({ children, userId, token, apiUrl = process.env.API_URL }) => {
        const [pusher, setPusher] = useState<Pusher | null>(null);

        useEffect(() => {
            if (!token) return undefined;

            const pusherInstance = new Pusher('8a871c98275b701001f6', {
                cluster: 'eu',
                ...getPusherSocketOptions(apiUrl),
                disableStats: true,
                authEndpoint: `${GlobalConstants.BaseUrl}/broadcasting/auth`,
                auth: {
                    headers: { Authorization: `Bearer ${token}` },
                },
            });
            pusherInstance.connect();

            setPusher(pusherInstance);

            return () => {
                if (pusherInstance) {
                    pusherInstance.disconnect();
                    setPusher(null);
                }
            };
        }, [userId, token, apiUrl]);

        const subscribeToChannel = useCallback(
            (channelName: string) => {
                if (!pusher) return null;

                return pusher.subscribe(channelName);
            },
            [pusher]
        );

        const unsubscribeFromChannel = useCallback(
            (channelName: string) => {
                if (pusher) {
                    pusher.unsubscribe(channelName);
                }
            },
            [pusher]
        );

        const value = useMemo<PusherContextValue>(
            () => ({
                pusher,
                subscribeToChannel,
                unsubscribeFromChannel,
            }),
            [pusher, subscribeToChannel, unsubscribeFromChannel]
        );

        return <PusherContext.Provider value={value}>{children}</PusherContext.Provider>;
    }
);

PusherProvider.displayName = 'PusherProvider';

export const usePusherContext = () => useContext(PusherContext);
